/**
 * TS-11.4 — supervisor delegation resolver.
 *
 * Pure functions, no DB. The supervisor-chain code (manufacturingAccess,
 * span-of-control filtering, approval routing) consults this layer
 * whenever it needs an "effective" supervisor instead of the static
 * worker_attributes.supervisor_id pointer.
 *
 * Status semantics (same as the SQL CHECK constraint):
 *   active   : effectiveFrom <= now < effectiveTo AND not revoked
 *   future   : effectiveFrom > now AND not revoked
 *   expired  : effectiveTo <= now AND not revoked
 *   revoked  : revokedAt set (regardless of effective range)
 */

import type {
  SupervisorDelegation,
  SupervisorDelegationStatus,
} from '../types/manufacturing-time.types'

/**
 * TS-11.4 — classify a delegation row by status relative to `asOf` (ISO
 * timestamp). Revocation wins over time bounds.
 */
export function classifyDelegation(
  d: SupervisorDelegation,
  asOf: string,
): SupervisorDelegationStatus {
  if (d.revokedAt) return 'revoked'
  if (asOf < d.effectiveFrom) return 'future'
  if (asOf >= d.effectiveTo) return 'expired'
  return 'active'
}

/**
 * TS-11.4 — find the active delegation for a delegator at `asOf`, or
 * undefined if none. When multiple are active (admin oversight), the
 * most-recently-created wins. This is a tie-break for safety, not a
 * supported workflow.
 */
export function findActiveDelegation(
  delegatorUserId: string,
  asOf: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
): SupervisorDelegation | undefined {
  const active = delegations
    .filter((d) => d.delegatorUserId === delegatorUserId)
    .filter((d) => classifyDelegation(d, asOf) === 'active')
  if (active.length === 0) return undefined
  return active.reduce((latest, cur) => (cur.createdAt > latest.createdAt ? cur : latest))
}

/**
 * TS-11.4 — return the effective supervisor user_id at `asOf`. When an
 * active delegation exists, returns the delegate; otherwise returns
 * the original. Caller passes the worker's static supervisor (from
 * worker_attributes.supervisor_id) — this helper is just the override.
 */
export function resolveEffectiveSupervisor(
  staticSupervisorId: string | undefined,
  asOf: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
): string | undefined {
  if (!staticSupervisorId) return undefined
  const active = findActiveDelegation(staticSupervisorId, asOf, delegations)
  return active ? active.delegateUserId : staticSupervisorId
}

/**
 * TS-11.4 — pre-build a delegator→delegate Map for hot-path callers
 * (span-of-control filtering iterates many workers, doesn't want O(n)
 * scans per worker). Only active delegations are included.
 */
export function buildActiveDelegationMap(
  asOf: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
): Map<string, string> {
  const out = new Map<string, string>()
  // Sort by createdAt asc so later entries overwrite earlier (tie-break)
  const sorted = [...delegations]
    .filter((d) => classifyDelegation(d, asOf) === 'active')
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))
  for (const d of sorted) out.set(d.delegatorUserId, d.delegateUserId)
  return out
}

/**
 * Time Approval Routing (Part 3) — delegators whose authority is delegated to
 * `delegateUserId` and active at `asOf`. Lets the approval queue surface a
 * delegator's items to the delegate.
 */
export function activeDelegatorsFor(
  delegateUserId: string,
  asOf: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
): string[] {
  return [...new Set(
    delegations
      .filter((d) => d.delegateUserId === delegateUserId && classifyDelegation(d, asOf) === 'active')
      .map((d) => d.delegatorUserId),
  )]
}

/** Half-open range overlap on ISO timestamps: [aFrom,aTo) intersects [bFrom,bTo). */
export function rangesOverlap(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  return aFrom < bTo && bFrom < aTo
}

/**
 * Time Approval Routing (Part 3) — the first non-revoked delegation for the same
 * delegator whose window overlaps [from,to), or undefined. Used to block a new
 * overlapping delegation (so a delegator never has two live coverage windows).
 * `excludeId` skips the row being edited.
 */
export function findOverlappingDelegation(
  delegatorUserId: string,
  from: string,
  to: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
  excludeId?: string,
): SupervisorDelegation | undefined {
  return delegations.find((d) =>
    d.id !== excludeId &&
    d.delegatorUserId === delegatorUserId &&
    !d.revokedAt &&
    rangesOverlap(from, to, d.effectiveFrom, d.effectiveTo),
  )
}
